import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

import { UploadService } from '@services/upload.service';
import { take } from 'rxjs';

@Component({
  selector: 'ciwc-order-add-attachment-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    NgIf,
    MatProgressSpinner,
    MatDialogContent,
    MatDialogTitle,
    MatDialogActions,
  ],
  templateUrl: './order-add-attachment-modal.component.html',
  styleUrls: ['./order-add-attachment-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderAddAttachmentModalComponent {
  private fb = inject(FormBuilder);
  private uploadService = inject(UploadService);

  attachmentForm: FormGroup;
  isUploading = signal(false);
  uploadError = signal('');

  constructor(
    private dialogRef: MatDialogRef<OrderAddAttachmentModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { rowIndex: number },
  ) {
    this.attachmentForm = this.fb.group({
      attachmentName: ['', Validators.required],
      attachmentDocument: ['', Validators.required],
    });
  }

  onSubmit(): void {
    if (this.attachmentForm.valid) {
      this.dialogRef.close({
        attachment: this.attachmentForm.value,
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  triggerFileInput() {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Update the form state immediately
      this.attachmentForm.patchValue({
        attachmentDocument: '',
      });

      // Set loading state
      this.isUploading.set(true);
      this.uploadError.set('');

      this.uploadService
        .uploadFile(file)
        .pipe(take(1))
        .subscribe({
          next: (res) => {
            // Update the form state with the response
            this.attachmentForm.patchValue({
              attachmentDocument: res.name,
            });

            // Reset loading state
            this.isUploading.set(false);
            this.uploadError.set('');
          },
          error: (err) => {
            // Update the form state with the error
            this.attachmentForm.patchValue({
              attachmentDocument: '',
            });

            // Set error state
            this.isUploading.set(false);
            this.uploadError.set('Failed to upload file. Please try again.');
          },
        });
    }
  }

  viewAttachment() {
    const attachmentUrl = this.attachmentForm.get('attachmentDocument')?.value;
    if (attachmentUrl) {
      window.open(attachmentUrl, '_blank');
    }
  }
}
